import { cartAtom } from "@/atoms"
import { getOrCreateSessionId } from "@/domain/storefront-analytics/session"
import {
  abandonedCartDelayMs,
  cancelAbandonedCartTimer,
  favoriteItemName,
  readAbandonedCartTimer,
  writeAbandonedCartTimer,
} from "@/domain/marketing/abandoned-cart"
import { postCartAbandoned } from "@/domain/marketing/api"
import { getMerchantId } from "@/lib/company-storage"
import { useAtomValue } from "jotai"
import { useEffect } from "react"
import { useAuthStatus } from "./use-auth-status"

/**
 * Enrols every identified customer in the abandoned-cart reminder once per session (opt-out model: the backend
 * suppresses numbers that replied "sair", as for campaigns): the bag must sit non-empty for 30 minutes without an
 * order. The timer lives in sessionStorage; an empty bag cancels it.
 */
export function useAbandonedCartReminder(): void {
  const cart = useAtomValue(cartAtom)
  const authenticated = useAuthStatus()
  const lines = cart.items
  const hasItems = lines.length > 0

  useEffect(() => {
    const merchantId = getMerchantId()
    if (!merchantId) return
    if (!hasItems) {
      cancelAbandonedCartTimer(merchantId)
      return
    }
    if (!authenticated) return

    let timer = readAbandonedCartTimer(merchantId)
    if (!timer) {
      timer = { startedAt: Date.now(), sent: false }
      writeAbandonedCartTimer(merchantId, timer)
    }
    const delay = abandonedCartDelayMs(timer, Date.now())
    if (delay === null) return

    const handle = window.setTimeout(() => {
      writeAbandonedCartTimer(merchantId, { startedAt: timer.startedAt, sent: true })
      void postCartAbandoned({
        consent: true,
        cartValueCents: Math.round(cart.totalPrice * 100),
        sessionId: getOrCreateSessionId(merchantId),
        favoriteItem: favoriteItemName(lines),
        abandonedAt: new Date().toISOString(),
      }).catch(() => undefined)
    }, delay)
    return () => window.clearTimeout(handle)
  }, [authenticated, hasItems, cart.totalPrice, lines])
}
